import { inject, Injectable } from "@angular/core";
import { CloseKirsCampaignGQL, CloseKirsCampaignMutation, ComparatorOperators, ConformanceToKiRsField, ConformanceToKiRsQueryComplexInput, ConformanceToKiRsSimpleQueryInput, ConformanceToKiRsUpdate, CreateOrUpdateKirsCampaignGQL, CreateOrUpdateKirsCampaignMutation, ExportKirsGQL, ExportKirsQuery, GetKirsCampaignsGQL, GetKirsCampaignsQuery, GetKirsGQL, GetKirsQuery, InitialMassiveEditKirsGQL, InitialMassiveEditKirsMutation, OperatorInput, PagingInput, UpdateKirsGQL, UpdateKirsMutation } from "../../../graphql/generated";
import { map, Observable } from "rxjs";

@Injectable({
    providedIn: 'root'
})
export class KirsService {
    private readonly GetKirsGQL: GetKirsGQL = inject(GetKirsGQL);
    private readonly GetKirsCampaignsGQL: GetKirsCampaignsGQL = inject(GetKirsCampaignsGQL);
    private readonly UpdateKirsGQL: UpdateKirsGQL = inject(UpdateKirsGQL);
    private readonly CreateOrUpdateKirsCampaignsGQL: CreateOrUpdateKirsCampaignGQL = inject(CreateOrUpdateKirsCampaignGQL)
    private readonly CloseKirsCampaignGQL: CloseKirsCampaignGQL = inject(CloseKirsCampaignGQL);
    private readonly ExportKirsGQL: ExportKirsGQL = inject(ExportKirsGQL);
    private readonly InitialMassiveEditKirsGQL: InitialMassiveEditKirsGQL = inject(InitialMassiveEditKirsGQL)

    getKirs(campaignId: string, parameters: any): Observable<GetKirsQuery['getConformanceKIRsAttestationsList']> {
        const params = convertParameters(parameters)
        return this.GetKirsGQL.fetch({ campaignId, page: params.page, query: params.query }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getConformanceKIRsAttestationsList))
    }

    getCampaigns(): Observable<GetKirsCampaignsQuery['getConformanceToKIRsCampaigns']> {
        return this.GetKirsCampaignsGQL.fetch({}, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getConformanceToKIRsCampaigns))
    }

    updateKirs(campaignId: string, input: ConformanceToKiRsUpdate): Observable<UpdateKirsMutation['updateConformanceToKIRsAttestation']> {
        return this.UpdateKirsGQL.mutate({ campaignId, input }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateConformanceToKIRsAttestation))
    }

    createOrUpdateCampaigns(campaignId: string, year: number, legalEntityTrackingIds: string[]): Observable<CreateOrUpdateKirsCampaignMutation['createOrUpdateConformanceToKIRsCampaign']> {
        return this.CreateOrUpdateKirsCampaignsGQL.mutate({ campaignId, year, legalEntityTrackingIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createOrUpdateConformanceToKIRsCampaign))
    }

    closeCampaign(id: string): Observable<CloseKirsCampaignMutation['closeConformanceToKIRsCampaign']> {
        return this.CloseKirsCampaignGQL.mutate({ id }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.closeConformanceToKIRsCampaign))
    }

    exportKirs(campaignId: string, timeZone: string, parameters: any): Observable<ExportKirsQuery['downloadConformanceKIRsAttestationsListExcel']> {
        const params = convertParameters(parameters)
        return this.ExportKirsGQL.fetch({ campaignId, query: params.query, timeZone }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.downloadConformanceKIRsAttestationsListExcel))
    }

    initialMassiveEdit(campaignId: string, legalEntityTrackingIds: string[], sourceLegalEntity: string): Observable<InitialMassiveEditKirsMutation['massiveEditConformanceToKIRsAttestation']> {
        return this.InitialMassiveEditKirsGQL.mutate({ campaignId, legalEntityTrackingIds, sourceLegalEntity }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.massiveEditConformanceToKIRsAttestation))
    }
}

export function convertParameters(parameters: any): { query: ConformanceToKiRsQueryComplexInput, page: PagingInput } {
    const query: ConformanceToKiRsSimpleQueryInput[] = [];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {
                //console.log"value", values);

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_KIRS[field as keyof typeof ENUM_KIRS])?.forEach(key => {

                        query.push({
                            field: key as ConformanceToKiRsField,
                            filters: {
                                operator: ComparatorOperators.Match,
                                value: (key !== ConformanceToKiRsField.LegalEntityCode ? key === ConformanceToKiRsField.OverallRatingQualitative ? values.map(v => String(v).replace(' ', '_')) : values : values.map(v => v?.split(/-(.+)/)[0]?.trim()))?.map(v => v?.toLowerCase() === 'empty' || v?.toLowerCase() === '' ? null : v)
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 1000000
        }
    }
}

export const ENUM_KIRS = {
    auditControlApproach: [ConformanceToKiRsField.AuditControlApproach],
    businessUnit: [ConformanceToKiRsField.BusinessUnit],
    chiefAuditor: [ConformanceToKiRsField.ChiefAuditor],
    endDate: [ConformanceToKiRsField.EndDate],
    legalEntityName: [ConformanceToKiRsField.LegalEntityCode],
    trackingId: [ConformanceToKiRsField.LegalEntityTrackingId],
    localChiefConfirmation: [ConformanceToKiRsField.LocalChiefConfirmation],
    notes: [ConformanceToKiRsField.Notes],
    omexNotes: [ConformanceToKiRsField.OmexNotes],
    organizationalUnit: [ConformanceToKiRsField.OrganizationalUnit],
    overallRatingQualitative: [ConformanceToKiRsField.OverallRatingQualitative],
    overallRatingQuantitative: [ConformanceToKiRsField.OverallRatingQuantitative],
    signed: [ConformanceToKiRsField.Signed],
    startDate: [ConformanceToKiRsField.StartDate],
    status: [ConformanceToKiRsField.Status],
    excerciseStatus: [ConformanceToKiRsField.ExcerciseStatus],
    timestamp: [ConformanceToKiRsField.ConfirmationDate]

}